import { c as _c } from "react/compiler-runtime";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import WaveDropsEmptyPlaceholder from "@/components/waves/drops/WaveDropsEmptyPlaceholder";
import { useUnreadDivider } from "@/contexts/wave/UnreadDividerContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ExtendedDrop } from "@/helpers/waves/drop.helpers";
import type { useVirtualizedWaveDrops } from "@/hooks/useVirtualizedWaveDrops";
import type { ActiveDropState } from "@/types/dropInteractionTypes";
import type { RefObject } from "react";
import { WaveDropsMessageListSection } from "./WaveDropsMessageListSection";
import { WaveDropsTypingIndicator } from "./WaveDropsTypingIndicator";
type WaveMessagesResult = ReturnType<typeof useVirtualizedWaveDrops>["waveMessages"];
interface WaveDropsContentProps {
  readonly waveMessages: WaveMessagesResult;
  readonly dropId: string | null;
  readonly scrollContainerRef: RefObject<HTMLDivElement | null>;
  readonly bottomAnchorRef: RefObject<HTMLDivElement | null>;
  readonly onTopIntersection: () => void;
  readonly onReply: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly onQuote: ({
    drop,
    partId
  }: {
    drop: ApiDrop;
    partId: number;
  }) => void;
  readonly queueSerialTarget: (serialNo: number) => void;
  readonly activeDrop: ActiveDropState | null;
  readonly serialTarget: number | null;
  readonly targetDropRef: RefObject<HTMLDivElement | null>;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly isAtBottom: boolean;
  readonly scrollToBottom: () => void;
  readonly typingMessage: string | null;
  readonly onDropContentClick?: ((drop: ExtendedDrop) => void) | undefined;
  readonly pendingCount: number;
  readonly onRevealPending: () => void;
  readonly bottomPaddingClassName?: string | undefined;
  readonly boostedDrops?: ApiDrop[] | undefined;
  readonly onBoostedDropClick?: ((serialNo: number) => void) | undefined;
}
export const WaveDropsContent: React.FC<WaveDropsContentProps> = t0 => {
  const $ = _c(30);
  const {
    waveMessages,
    dropId,
    scrollContainerRef,
    bottomAnchorRef,
    onTopIntersection,
    onReply,
    onQuote,
    queueSerialTarget,
    activeDrop,
    serialTarget,
    targetDropRef,
    onQuoteClick,
    isAtBottom,
    scrollToBottom,
    typingMessage,
    onDropContentClick,
    pendingCount,
    onRevealPending,
    bottomPaddingClassName,
    boostedDrops,
    onBoostedDropClick
  } = t0;
  const {
    unreadDividerSerialNo
  } = useUnreadDivider();
  const dropsCount = waveMessages?.drops.length ?? 0;
  const isInitialLoading = !!waveMessages?.isLoading && !waveMessages.isLoadingNextPage && dropsCount === 0;
  const isHydrating = !waveMessages;
  if (isHydrating || isInitialLoading) {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-10"><CircleLoader size={CircleLoaderSize.XXLARGE} /></div>;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (dropsCount === 0) {
    let t1;
    if ($[1] !== dropId) {
      t1 = <WaveDropsEmptyPlaceholder dropId={dropId} />;
      $[1] = dropId;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    return t1;
  }
  let t1;
  if ($[3] !== activeDrop || $[4] !== boostedDrops || $[5] !== bottomAnchorRef || $[6] !== bottomPaddingClassName || $[7] !== dropId || $[8] !== isAtBottom || $[9] !== onBoostedDropClick || $[10] !== onDropContentClick || $[11] !== onQuote || $[12] !== onQuoteClick || $[13] !== onReply || $[14] !== onRevealPending || $[15] !== onTopIntersection || $[16] !== pendingCount || $[17] !== queueSerialTarget || $[18] !== scrollContainerRef || $[19] !== scrollToBottom || $[20] !== serialTarget || $[21] !== targetDropRef || $[22] !== unreadDividerSerialNo || $[23] !== waveMessages) {
    t1 = <WaveDropsMessageListSection waveMessages={waveMessages} dropId={dropId} scrollContainerRef={scrollContainerRef} bottomAnchorRef={bottomAnchorRef} onTopIntersection={onTopIntersection} onReply={onReply} onQuote={onQuote} queueSerialTarget={queueSerialTarget} activeDrop={activeDrop} serialTarget={serialTarget} targetDropRef={targetDropRef} onQuoteClick={onQuoteClick} isAtBottom={isAtBottom} scrollToBottom={scrollToBottom} onDropContentClick={onDropContentClick} pendingCount={pendingCount} onRevealPending={onRevealPending} bottomPaddingClassName={bottomPaddingClassName} unreadDividerSerialNo={unreadDividerSerialNo} boostedDrops={boostedDrops} onBoostedDropClick={onBoostedDropClick} />;
    $[3] = activeDrop;
    $[4] = boostedDrops;
    $[5] = bottomAnchorRef;
    $[6] = bottomPaddingClassName;
    $[7] = dropId;
    $[8] = isAtBottom;
    $[9] = onBoostedDropClick;
    $[10] = onDropContentClick;
    $[11] = onQuote;
    $[12] = onQuoteClick;
    $[13] = onReply;
    $[14] = onRevealPending;
    $[15] = onTopIntersection;
    $[16] = pendingCount;
    $[17] = queueSerialTarget;
    $[18] = scrollContainerRef;
    $[19] = scrollToBottom;
    $[20] = serialTarget;
    $[21] = targetDropRef;
    $[22] = unreadDividerSerialNo;
    $[23] = waveMessages;
    $[24] = t1;
  } else {
    t1 = $[24];
  }
  let t2;
  if ($[25] !== typingMessage) {
    t2 = <WaveDropsTypingIndicator typingMessage={typingMessage} />;
    $[25] = typingMessage;
    $[26] = t2;
  } else {
    t2 = $[26];
  }
  let t3;
  if ($[27] !== t1 || $[28] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[27] = t1;
    $[28] = t2;
    $[29] = t3;
  } else {
    t3 = $[29];
  }
  return t3;
};